import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import { Link } from "react-router-dom";
import ConfirmationModal from "../../components/ConfirmationModal";
import AppPageTitle from "../../components/AppPageTitle";
import AppTableActionMenu from "../../components/AppTableActionMenu";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AgentAvatar } from "./avatarOptions";

function IconPlus({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="12" y1="5" x2="12" y2="19" />
			<line x1="5" y1="12" x2="19" y2="12" />
		</svg>
	);
}

function IconSearch() {
	return (
		<svg className="size-4 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconBook({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
			<path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
		</svg>
	);
}

function IconEdit({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 20h9" />
			<path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
		</svg>
	);
}

function IconDelete({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function IconMore() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="1" />
			<circle cx="19" cy="12" r="1" />
			<circle cx="5" cy="12" r="1" />
		</svg>
	);
}

function IconSpark({ className = "size-3.5 shrink-0 text-primary" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="currentColor">
			<path d="m12 2 2.2 5.8L20 10l-5.8 2.2L12 18l-2.2-5.8L4 10l5.8-2.2Z" />
		</svg>
	);
}

const PAGE_SIZE = 10;
const MAX_AGENTES_IA = 4;

function formatSetor(agent) {
	if (agent?.setorAtribuido == null || agent?.setorAtribuido === "") return "—";
	return `#${agent.setorAtribuido}`;
}

export function OpenAi() {
	const [loadingPage, setLoadingPage] = useState(false);
	const [page, setPage] = useState(1);
	const [agentes, setAgentes] = useState([]);
	const [hasMore, setHasMore] = useState(true);
	const [open, setOpen] = useState(false);
	const [selectedId, setSelectedId] = useState(null);
	const [searchParam, setSearchParam] = useState("");

	const handleOpenConfirm = (id) => {
		setSelectedId(id);
		setOpen(true);
	};

	const handleCloseConfirm = () => {
		setOpen(false);
		setSelectedId(null);
	};

	const handleConfirmDelete = async () => {
		try {
			await api.delete(`/excluirAgente/${selectedId}`);
			setAgentes((prev) => prev.filter((agent) => agent.id !== selectedId));
			toast.success("Agente removido com sucesso.");
		} catch (err) {
			toastError(err);
		} finally {
			handleCloseConfirm();
		}
	};

	const fetchAgentes = async (currentPage) => {
		setLoadingPage(true);
		try {
			const { data } = await api.post("/listarAgentes", {
				page: currentPage,
			});

			const nextData = Array.isArray(data) ? data : [];
			setHasMore(nextData.length === PAGE_SIZE);
			setAgentes((prev) =>
				currentPage === 1 ? nextData : [...prev, ...nextData]
			);
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingPage(false);
		}
	};

	useEffect(() => {
		fetchAgentes(page);
	}, [page]);

	const filteredAgentes = useMemo(() => {
		const term = searchParam.toLowerCase().trim();
		if (!term) return agentes;

		return agentes.filter((agent) =>
			[
				agent.id,
				agent.nome,
				agent.objetivo,
				agent.personalidade,
				agent.linguagem,
				agent.setorAtribuido,
			]
				.filter(Boolean)
				.join(" ")
				.toLowerCase()
				.includes(term)
		);
	}, [agentes, searchParam]);

	const atAgentLimit = agentes.length >= MAX_AGENTES_IA;

	return (
		<div className="app-admin-page">
			<ConfirmationModal
				title="Excluir agente"
				open={open}
				onClose={setOpen}
				onConfirm={handleConfirmDelete}
			>
				Tem certeza que deseja excluir este agente? Essa ação não pode ser desfeita.
			</ConfirmationModal>

			<AppPageTitle title="Agentes IA" subtitle="Menu" />

			<div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
				Limite da empresa: no máximo <strong>{MAX_AGENTES_IA}</strong> agentes de IA
				{agentes.length > 0 ? ` (${agentes.length}/${MAX_AGENTES_IA} em uso)` : ""}.
				{atAgentLimit
					? " Remova um agente existente para criar outro."
					: ""}
			</div>

			<div className="mb-5 flex flex-wrap items-center justify-between gap-3">
				<div className="relative w-full md:max-w-xs">
					<input
						type="search"
						className="form-input form-input-sm w-full ps-11"
						placeholder="Buscar agentes..."
						value={searchParam}
						onChange={(e) => setSearchParam(e.target.value)}
					/>
					<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
						<IconSearch />
					</div>
				</div>

				{atAgentLimit ? (
					<button
						type="button"
						disabled
						className="btn btn-sm shrink-0 cursor-not-allowed bg-default-300 text-default-600 opacity-70 dark:bg-default-700 dark:text-default-300"
						title={`Limite de ${MAX_AGENTES_IA} agentes atingido`}
					>
						<IconPlus className="size-4 ms-1" />
						Limite atingido
					</button>
				) : (
					<Link
						to="/novoAgente"
						className="btn btn-sm shrink-0 bg-primary text-white no-underline"
					>
						<IconPlus className="size-4 ms-1" />
						Adicionar Agente
					</Link>
				)}
			</div>

			{loadingPage && agentes.length === 0 ? (
				<div className="app-modal-loading py-16">
					<div className="app-modal-spinner" />
				</div>
			) : filteredAgentes.length === 0 ? (
				<div className="card">
					<div className="card-body flex flex-col items-center justify-center gap-2 py-16 text-center">
						<p className="m-0 font-semibold text-default-800 dark:text-default-100">
							Nenhum agente cadastrado
						</p>
						<p className="m-0 text-sm text-default-500 dark:text-default-400">
							{searchParam
								? "Tente outro termo de busca."
								: "Crie seu primeiro agente IA para começar."}
						</p>
					</div>
				</div>
			) : (
				<div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
					{filteredAgentes.map((agent) => (
						<div key={agent.id} className="card">
							<div className="card-body">
								<div className="mb-4 flex items-center justify-between gap-5">
									<IconSpark className="size-5 text-warning fill-warning/10" />

									<AppTableActionMenu
										triggerIcon={<IconMore />}
										triggerAriaLabel={`Ações do agente ${agent.nome}`}
										items={[
											{
												key: "knowledge",
												label: "Conhecimentos",
												icon: <IconBook className="size-3 shrink-0" />,
												onClick: () => {
													window.location.href = `/ConhecimentoIa/?${agent.id}`;
												},
											},
											{
												key: "edit",
												label: "Editar",
												icon: <IconEdit className="size-3 shrink-0" />,
												onClick: () => {
													window.location.href = `/novoAgente/?edit=${agent.id}`;
												},
											},
											{
												key: "delete",
												label: "Excluir",
												icon: <IconDelete className="size-3 shrink-0" />,
												tone: "danger",
												onClick: () => handleOpenConfirm(agent.id),
											},
										]}
									/>
								</div>

								<div>
									<div className="mx-auto flex items-center justify-center">
										<AgentAvatar
											avatarId={agent.avatar}
											className="size-16 rounded-full outline outline-4 outline-default-100 outline-offset-4 dark:outline-default-800"
											iconClassName="size-8"
										/>
									</div>

									<div className="mt-4 text-center">
										<h6 className="text-base font-semibold text-default-800 dark:text-default-100">
											{agent.nome}
										</h6>
										<p className="text-sm text-default-500 dark:text-default-400">
											Agente virtual
										</p>
									</div>

									<div className="mb-4 mt-3 flex min-h-[28px] items-center justify-center">
										{formatSetor(agent) !== "—" ? (
											<span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
												<IconSpark className="size-3 shrink-0 text-primary" />
												Setor {formatSetor(agent)}
											</span>
										) : null}
									</div>

									<div className="grid grid-cols-2">
										<div className="border-e border-dashed border-default-200 p-3 text-center dark:border-white/14">
											<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
												Objetivo
											</p>
											<p className="m-0 line-clamp-4 text-sm leading-6 text-default-700 dark:text-default-200">
												{agent.objetivo || "Não informado."}
											</p>
										</div>

										<div className="p-3 text-center">
											<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
												Personalidade
											</p>
											<p className="m-0 line-clamp-4 text-sm leading-6 text-default-700 dark:text-default-200">
												{agent.personalidade || "Não informada."}
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			)}

			{agentes.length > 0 && (
				<div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-default-500 md:justify-between">
					<p className="text-sm text-default-500 dark:text-default-400">
						Exibindo <b>{filteredAgentes.length}</b> agente(s) carregado(s)
					</p>
					{hasMore && (
						<button
							type="button"
							className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-white/14 dark:text-default-300"
							onClick={() => setPage((prev) => prev + 1)}
							disabled={loadingPage}
						>
							{loadingPage ? "Carregando..." : "Carregar mais"}
						</button>
					)}
				</div>
			)}
		</div>
	);
}
